import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split, Stack} from '@dropbox/dig-foundations';
import {Avatar} from 'components/DSYS/Avatar';
import {LabelGroup} from 'components/DSYS/LabelGroup';
import {ButtonLink} from 'components/DSYS/Link';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {ROUTE_PATHS} from 'constant';
import {useProfile} from 'hooks/useProfile';
import {t} from 'i18next';
import {
  dashboardLinkDestination,
  logDashboardInteraction,
} from 'views/home/dashboard/dashboardAnalytics';
import {DashboardState} from 'views/home/dashboard/dashboardModel';
import {Tile} from 'views/home/dashboard/Tile';

/** Max lines of "Working with me" shown in the meet-someone tile. */
const ABOUT_ME_MAX_LINES = 3;

export const MeetSomeoneTile = ({state}: {state: DashboardState}) => {
  const person = state.featuredPerson;
  const {data: profile} = useProfile({ldap: person?.ldap, throwOnError: false});
  const workingWithMe = profile?.workingWithMe;
  const bodyRef = useSkeletonFadeIn(state.meetSomeoneLoading);

  if (state.meetSomeoneLoading) {
    return (
      <Tile tileId="meetSomeone">
        <Stack gap="12" style={{height: '100%'}}>
          <Split gap="10" alignY="center">
            <Split.Item>
              <Skeleton.Avatar size="medium" />
            </Split.Item>
            <Split.Item width="fill">
              <Stack gap="4">
                <Skeleton.Text size="small" width={120} />
                <Skeleton.Text size="xsmall" width={90} />
              </Stack>
            </Split.Item>
          </Split>
          <Skeleton.Box width={120} height={24} withBorderRadius="medium" />
          <Stack gap="4">
            <Skeleton.Text width={220} />
            <Skeleton.Text width={200} />
          </Stack>
        </Stack>
      </Tile>
    );
  }

  if (!person) {
    return (
      <Tile tileId="meetSomeone" bodyRef={bodyRef}>
        <Text color="faint">{t('dashboard_meet_someone_empty')}</Text>
      </Tile>
    );
  }

  const roleLine = [person.role, person.level].filter(Boolean).join(' · ');
  const profileHref = person.ldap
    ? ROUTE_PATHS.PROFILE.replace(':userid', person.ldap)
    : ROUTE_PATHS.PEOPLE;
  const coffeeChatButton = (
    <ButtonLink
      to={profileHref}
      variant="outline"
      size="small"
      onClick={() =>
        logDashboardInteraction({
          action: 'row_click',
          tileId: 'meetSomeone',
          target: 'coffee_chat',
          destination: dashboardLinkDestination(profileHref),
        })
      }
    >
      {t('dashboard_schedule_coffee_chat')}
    </ButtonLink>
  );

  return (
    <Tile tileId="meetSomeone" bodyRef={bodyRef}>
      <Stack gap="12" style={{height: '100%'}}>
        <LabelGroup
          align="top"
          withLeftAccessory={
            <Avatar
              size="medium"
              user={
                {name: person.name, ldap: person.ldap, profile_photo_url: person.photoUrl} as never
              }
            />
          }
          withText={
            <Text isBold tagName="div" style={{minWidth: 0}}>
              <Truncate observeParent>{person.name}</Truncate>
            </Text>
          }
          withSubtext={
            <Stack gap="0">
              {roleLine && (
                <Text size="small" color="faint" tagName="div" style={{minWidth: 0}}>
                  <Truncate observeParent>{roleLine}</Truncate>
                </Text>
              )}
              {person.team && (
                <Text size="small" color="faint" tagName="div" style={{minWidth: 0}}>
                  <Truncate observeParent>{person.team}</Truncate>
                </Text>
              )}
            </Stack>
          }
        />

        {person.connection && (
          <Box
            display="flex"
            alignItems="center"
            paddingX="10"
            paddingY="4"
            borderRadius="Circular"
            backgroundColor="Opacity Surface"
            width="fit-content"
          >
            <Text size="small" color="subtle" tagName="div" style={{minWidth: 0, maxWidth: '100%'}}>
              <Truncate observeParent>{person.connection}</Truncate>
            </Text>
          </Box>
        )}

        {workingWithMe && (
          <Box minHeight="0" flexGrow={1}>
            <RichTextArea
              value={workingWithMe}
              editable={false}
              truncateLines={ABOUT_ME_MAX_LINES}
              textColor="subtle"
              textSize="small"
              maxWidth={false}
            />
          </Box>
        )}

        {coffeeChatButton}
      </Stack>
    </Tile>
  );
};
